import { Select } from '@/components/ui/select';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBranchScope } from '@/hooks/useBranchScope';
import useSWR from 'swr';
import { ChevronDown, ChevronRight, ExternalLink, Loader2, Pencil } from 'lucide-react';
import UnifiedEditForm from '@/components/UnifiedEditForm';
import { loadPolygonData } from '@/utils/polygonLoader';
import { DatePicker } from '@/components/ui/date-picker';
import { daysAgo } from './ChartPeriod';

import { Card, CardContent } from '@/components/ui/card';
import { FrameWithPolygon } from '@/components/ui/frame-with-polygon';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { useCurrentZone, useZonesList } from '@/hooks/useCurrentZone';
import type { ZoneDto } from '@/api/zones';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useAppActions, useAppState } from '@/app/appContext';
import { patchRack } from '@/api/racks';
import { patchShelf } from '@/api/shelves';
import { patchCheckoutQueue } from '@/api/checkoutControl';
import { patchTable } from '@/api/tables';
import {
  getCalibrationPolygons,
  getCalibrationExamples,
  type CalibrationPolygon,
  type CalibrationPolygonKind,
  type CalibrationSeverity,
  type CalibrationEdit,
} from '@/api/calibration';
import { cn } from '@/lib/utils';
import ShelfUsefulAreaDialog from './ShelfUsefulAreaDialog';
import ShelfThresholdChart from './ShelfThresholdChart';
import TrackDistributionChart from './TrackDistributionChart';
import ThresholdScheduleCard from './ThresholdScheduleCard';
import QueuePolygonEditor from './QueuePolygonEditor';
import ModelSwitchDialog, { type ModelChoice } from './ModelSwitchDialog';
import { MODEL_LABEL, type ShelfModel } from '@/api/calibrationChart';
import { Tooltip } from '@/components/ui/tooltip';

// #159 — «Калибровка полигонов»: каркас. One collapsed row per polygon of the
// branch; expand for a 5-examples-per-state matrix, threshold input, and a
// working deep-link into Зонирование. #160 filled in the per-track matrix
// content (queue/cashier/staff/order_waiting: person count + boxes; shelf/
// rack: 11 fill-% tiers off their own crop, no boxes — frame-space boxes
// would misalign on a crop and there's no person to box anyway; table: the
// 4 real gates — occupied/dirty/shifted/free, off TableOccupancy, corrected
// by TableOccupancyMarked the same way the strips chart is). #161 added the
// bad-threshold diagnosis: colored text on the COLLAPSED row (visible
// without expanding) + bad rows sorted to the top of the whole list, reusing
// the same verdicts/cutoffs as linzahelper's morning digest
// (monitor/analysis_digest.py + monitor/digest_sql/*.sql), computed directly
// from occupancy readings rather than the digest's validator-gated queries
// so it also catches polygons nobody has corrected recently. Still NOT done
// (deferred, out of this pass): showing the bad cases as extra example rows
// inside the matrix itself (#160/#161's shared "покажите примерами" bullet).
//
// The window is ALWAYS capped to the last 2 weeks (#156: catching drift is
// the whole point) — the per-row "from" picker can only narrow that window,
// never extend it. There's no `updated_at` on PolygonArea/Rack/Shelf/Table/
// CheckoutZone to default it to hard "was this polygon just moved" (checked
// during implementation — none of these models track a last-vertex-edit
// timestamp anywhere in the schema), so today it just defaults to 14 days
// back; adding that timestamp is a real, separate DB-schema decision flagged
// to Alexey rather than made silently here.



// Сила отметок (Алексей 27.10 #3003): много отмен алертов и много исправлений в отчётах — красным,
// остальное по силе — оранжевым или бледным.
const FLAG_CHIP: Record<CalibrationSeverity, string> = {
  high: 'bg-red-500/15 text-red-700 dark:text-red-300',
  mid: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  low: 'bg-muted text-muted-foreground',
  info: 'px-0 font-normal text-muted-foreground',
};
const DIAG_TEXT: Record<CalibrationSeverity, string> = {
  high: 'text-red-600 dark:text-red-400',
  mid: 'text-amber-600 dark:text-amber-400',
  low: 'text-muted-foreground',
  info: 'text-muted-foreground',
};
const SEVERITY_RANK: Record<CalibrationSeverity, number> = { high: 0, mid: 1, low: 2, info: 3 };

const KIND_LABELS: Record<CalibrationPolygonKind, string> = {
  rack: 'Витрина', shelf: 'Полка', table: 'Стол',
  queue: 'Очередь', cashier: 'Касса', staff: 'Сотрудники', order_waiting: 'Ожидание заказа',
};

const KIND_ORDER: CalibrationPolygonKind[] = ['queue', 'cashier', 'staff', 'order_waiting', 'rack', 'shelf', 'table'];

// Подписи порогов. Раньше здесь печатались имена полей как есть — «empty_shelf_score»
// рядом с «max_effective_percentage», и по ним невозможно понять, что одно это норма
// алерта, а другое физический потолок полки, на который делится ВСЁ остальное.
// AI197 (#1165): у полки в основной строке — поправка модели и нижняя граница нормы (порог
// алерта); useful_area_pct — полезная площадь, размечается контуром; делитель — что реально
// применяется сейчас (поправка → полезная площадь → 0.8).
const THRESHOLD_SHORT: Record<string, string> = {
  empty_shelf_score: 'порог',
  model_correction_max_pct: 'поправка модели',
  useful_area_pct: 'полезная площадь',
  divisor: 'делитель',
  max_allowed_size: 'длинная очередь от',
};
const SHELF_ROW_KEYS = ['empty_shelf_score', 'model_correction_max_pct', 'divisor'];

/** Свёрнутая строка: пороги одной строкой. Полке — только то, что решает (порог, поправка,
 *  делитель) и нейросеть; стеллажу — нейросеть и «настроено по полкам». */
function rowSummary(polygon: CalibrationPolygon, shelfCount?: number): string {
  const fmt = (k: string, v: number | boolean) =>
    typeof v === 'boolean' ? (v ? 'да' : 'нет')
      : (k.includes('percentage') || k.includes('score') || k.includes('pct') || k === 'divisor') ? `${Math.round(Number(v) * 100)}%` : String(v);
  const parts: string[] = [];
  if (polygon.kind === 'shelf') {
    for (const k of SHELF_ROW_KEYS) {
      const v = polygon.thresholds[k];
      if (v != null) parts.push(`${THRESHOLD_SHORT[k]} ${fmt(k, v)}`);
    }
    if (polygon.model) {
      parts.push(`нейросеть: ${MODEL_LABEL[polygon.model]}${polygon.shelf_predict_percentage == null ? '' : ' (своя)'}`);
    }
  } else if (polygon.kind === 'rack') {
    if (shelfCount != null) parts.push(`полок: ${shelfCount}`);
    if (polygon.model) parts.push(`нейросеть: ${MODEL_LABEL[polygon.model]}`);
    if (polygon.shelves_configured) parts.push('настроено по полкам');
  } else if (polygon.kind === 'table') {
    // Порог алерта «стол грязный»: свой или системный (AI214).
    const eff = polygon.thresholds.alert_threshold;
    if (eff != null) parts.push(`порог алерта ${Math.round(Number(eff) * 100)}%${polygon.thresholds.alert_min_dirty_conf == null ? ' (системный)' : ' (свой)'}`);
  } else {
    // Очередь: алерт, когда людей БОЛЬШЕ max_allowed_size → «от N+1», как в строке порога и на графике
    // (Эдем 29.10: в шапке было «от 5», в строке «от 6»).
    for (const [k, v] of Object.entries(polygon.thresholds)) {
      parts.push(k === 'max_allowed_size' ? `${THRESHOLD_SHORT[k]} ${Number(v) + 1}` : `${THRESHOLD_SHORT[k] ?? k} ${fmt(k, v)}`);
    }
  }
  return parts.join(' · ');
}

/** Выбор нейросети полки/стеллажа — через диалог с предупреждением (AI197). */
function ModelPicker({ polygon, rackModel, onSaved }: { polygon: CalibrationPolygon; rackModel?: ShelfModel; onSaved: () => void }) {
  const isShelf = polygon.kind === 'shelf';
  const current: ModelChoice = isShelf
    ? (polygon.shelf_predict_percentage == null ? 'inherit' : polygon.shelf_predict_percentage ? 'pct' : 'yolo')
    : (polygon.thresholds.predict_percentage ? 'pct' : 'yolo');
  const [pending, setPending] = useState<ModelChoice | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!pending || !polygon.threshold_entity_id) return;
    setBusy(true);
    try {
      if (isShelf) {
        await patchShelf(polygon.threshold_entity_id, { predict_percentage: pending === 'inherit' ? null : pending === 'pct' });
      } else {
        await patchRack(polygon.threshold_entity_id, { predict_percentage: pending === 'pct' });
      }
      setPending(null);
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  const inheritLabel = rackModel ? `как у стеллажа (${MODEL_LABEL[rackModel]})` : 'как у стеллажа';
  return (
    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
      Нейросеть
      <span className="flex items-center gap-2">
        <Select
          wrapperClassName="w-auto"
          value={current}
          onChange={(e) => { const v = e.target.value as ModelChoice; if (v !== current) setPending(v); }}
          className="h-8 text-foreground"
        >
          {isShelf && <option value="inherit">{inheritLabel}</option>}
          <option value="yolo">{MODEL_LABEL.yolo} (боксы, ширина)</option>
          <option value="pct">{MODEL_LABEL.pct} (площадь)</option>
        </Select>
        {!isShelf && polygon.shelves_configured && (
          <Tooltip content="У части полок своя нейросеть"><span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
            Настроено по полкам
          </span></Tooltip>
        )}
      </span>
      {pending && (
        <ModelSwitchDialog
          open
          onOpenChange={(v) => { if (!v) setPending(null); }}
          scope={isShelf ? 'shelf' : 'rack'}
          from={current}
          to={pending}
          shelvesConfigured={polygon.shelves_configured}
          busy={busy}
          onConfirm={confirm}
        />
      )}
    </label>
  );
}

/** Маленькое значение «как текст», но редактируется на месте: клик → поле, Enter/blur —
 *  сохранить, Esc — отмена (#1189/#1190). Без кнопки «Сохранить» (#1187). */
function InlineValue({ value, display, onSave, kind = 'number', title, placeholder }: {
  value: number | string | null;
  display: string;
  onSave: (v: number | string | null) => Promise<void>;
  kind?: 'number' | 'text';
  title?: string;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const start = () => { setDraft(value == null ? '' : String(value)); setEditing(true); };
  const commit = async () => {
    setEditing(false);
    const next = kind === 'number' ? (draft.trim() === '' ? null : Number(draft)) : draft.trim();
    if (next === value || (kind === 'number' && next != null && Number.isNaN(next))) return;
    setBusy(true);
    try { await onSave(next); } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <input
        autoFocus
        type={kind === 'number' ? 'number' : 'text'}
        step={kind === 'number' ? 0.01 : undefined}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { void commit(); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { void commit(); } else if (e.key === 'Escape') setEditing(false); }}
        className={`${kind === 'number' ? 'w-20' : 'w-48'} rounded border border-border bg-background px-1 py-0 text-xs text-foreground`}
      />
    );
  }
  return (
    <Tooltip content={title ?? 'Нажмите, чтобы изменить'}><button
      type="button"
      onClick={start}
      disabled={busy}
      className="rounded px-1 text-xs text-foreground underline decoration-dotted decoration-muted-foreground/60 underline-offset-2 hover:bg-muted disabled:opacity-50"
    >
      {busy ? '…' : display}
    </button></Tooltip>
  );
}

const pctText = (v: number | boolean | string | null | undefined) => (v == null || v === '' ? '—' : `${Math.round(Number(v) * 100)}%`);

/** Шапка раскрытого ряда: маленькая строка значений, каждое правится на месте; порог и делитель
 *  полки двигаются и маркерами на графике. Кнопки «Сохранить» нет (#1187). */
function ThresholdFields({ polygon, rackModel, onSaved }: { polygon: CalibrationPolygon; rackModel?: ShelfModel; onSaved: () => void }) {
  const [builderOpen, setBuilderOpen] = useState(false);
  const entityId = polygon.threshold_entity_id;
  const t = polygon.thresholds;
  const Sep = () => <span className="text-muted-foreground/60">·</span>;
  const nameEditor = (polygon.kind === 'rack' || polygon.kind === 'shelf') && entityId ? (
    <InlineValue
      kind="text" value={polygon.name} display={polygon.name} title="Переименовать"
      onSave={async (v) => {
        if (polygon.kind === 'rack') await patchRack(entityId, { name: String(v ?? '') });
        else await patchShelf(entityId, { name: String(v ?? '') });
        onSaved();
      }}
    />
  ) : null;

  if (polygon.kind === 'rack') {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>имя</span>{nameEditor}<Sep />
        <ModelPicker polygon={polygon} onSaved={onSaved} />
        <Sep /><span>пороги — у полок (ниже)</span>
      </div>
    );
  }
  if (polygon.kind === 'shelf' && entityId) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>имя</span>{nameEditor}<Sep />
        <ModelPicker polygon={polygon} rackModel={rackModel} onSaved={onSaved} /><Sep />
        <Tooltip content="empty_shelf_score — нижняя граница нормы в eff (после делителя); на графике — красный маркер"><span>порог (eff)</span></Tooltip>
        <InlineValue value={Number(t.empty_shelf_score)} display={pctText(t.empty_shelf_score)}
          onSave={async (v) => { await patchShelf(entityId, { empty_shelf_score: Number(v) }); onSaved(); }} />
        <Sep />
        <Tooltip content="model_correction_max_pct — делитель под модель; на графике — серый маркер (eff 100%)"><span>поправка модели</span></Tooltip>
        <InlineValue value={t.model_correction_max_pct == null ? null : Number(t.model_correction_max_pct)} display={pctText(t.model_correction_max_pct)} placeholder="нет"
          onSave={async (v) => { await patchShelf(entityId, { model_correction_max_pct: v == null ? null : Number(v) }); onSaved(); }} />
        <Sep />
        <Tooltip content="useful_area_pct — где на полке физически может лежать товар, доля кропа; размечается контуром"><span>
          полезная площадь {pctText(t.useful_area_pct)}
          {polygon.useful_area_mode && <span className="text-muted-foreground/70"> ({polygon.useful_area_mode === 'include' ? 'обведено полезное' : 'обведено лишнее'})</span>}
        </span></Tooltip>
        <Sep />
        <Tooltip content="Что реально делит сырое число: поправка → полезная площадь → 0.8"><span>делитель <b className="text-foreground">{pctText(t.divisor)}</b></span></Tooltip>
        <button type="button" onClick={() => setBuilderOpen(true)} className="rounded-md border border-border px-2 py-0.5 text-[11px] text-foreground hover:bg-muted">
          Разметить полезную площадь
        </button>
        <ShelfUsefulAreaDialog shelfId={Number(entityId)} open={builderOpen} onOpenChange={setBuilderOpen} onApplied={onSaved} />
      </div>
    );
  }
  if (polygon.kind === 'queue' && entityId) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>длинная очередь от</span>
        <InlineValue value={Number(t.max_allowed_size) + 1} display={`${Number(t.max_allowed_size) + 1} чел.`}
          onSave={async (v) => { await patchCheckoutQueue(entityId, { max_allowed_size: Math.max(0, Number(v) - 1) }); onSaved(); }} />
        <span>(max_allowed_size {String(t.max_allowed_size)}; на графике — красный маркер)</span>
      </div>
    );
  }
  if (polygon.kind === 'table' && entityId) {
    // Порог алерта «стол грязный» per-table (AI214, #1306): свой или системный; на графике — красный маркер.
    const own = t.alert_min_dirty_conf;
    const eff = Number(t.alert_threshold);
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Tooltip content="alert_min_dirty_conf — уверенность модели, от которой уходит алерт об уборке; пусто = системный порог"><span>порог алерта «стол грязный»</span></Tooltip>
        <InlineValue value={own == null ? null : Math.round(Number(own) * 100)} display={`${Math.round(eff * 100)}%${own == null ? ' (системный)' : ''}`}
          placeholder="системный"
          onSave={async (v) => {
            const n = v == null || v === '' ? null : Math.min(99, Math.max(5, Number(v))) / 100;
            await patchTable(entityId, { alert_min_dirty_conf: n }); onSaved();
          }} />
        {own != null && (
          <button type="button" className="rounded border border-border px-1.5 py-0.5 hover:bg-muted"
            onClick={async () => { await patchTable(entityId, { alert_min_dirty_conf: null }); onSaved(); }}>
            сбросить на системный
          </button>
        )}
        <span>(на графике — красный маркер, перетаскивается)</span>
      </div>
    );
  }
  return <div className="text-xs text-muted-foreground">У этого типа полигона нет порогов для настройки здесь.</div>;
}

function ExampleCell({ example }: { example: { image_url: string; boxes: { x0: number; y0: number; x1: number; y1: number }[]; polygons?: number[][][] } }) {
  // Пропорции фото — свои, не 16:9 (Алексей 09.10, #1372): до загрузки держим 16:9,
  // потом ячейка принимает естественное соотношение кадра/кропа.
  const [ar, setAr] = useState<number | null>(null);
  return (
    <div className="overflow-hidden rounded-md border border-border bg-muted" style={{ aspectRatio: ar ?? 16 / 9 }}>
      <FrameWithPolygon src={example.image_url} polygons={example.polygons ?? []} boxes={example.boxes} alt="" strokeWidth={2} onAspect={setAr} />
    </div>
  );
}

function CalibrationMatrix({ polygon, zoneId, from }: { polygon: CalibrationPolygon; zoneId: string; from: string }) {
  const to = useState(() => new Date().toISOString())[0];
  const { data, isLoading } = useSWR(
    ['calibration:examples', polygon.kind, polygon.id, zoneId, from, to],
    () => getCalibrationExamples(polygon.kind, polygon.id, zoneId, from, to).then((r) => r.data),
    { revalidateOnFocus: false },
  );

  if (isLoading) {
    return <div className="flex h-24 items-center justify-center text-muted-foreground"><Loader2 className="size-5 animate-spin" aria-hidden /></div>;
  }
  if (!data || data.not_implemented) {
    return (
      <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
        Матрица примеров для трека «{KIND_LABELS[polygon.kind]}» пока не реализована. Порог и переход в Зонирование уже работают.
      </div>
    );
  }
  if (data.rows.length === 0) {
    return <div className="text-xs text-muted-foreground">Нет данных за выбранный период.</div>;
  }

  return (
    <div className="space-y-2">
      {polygon.kind === 'shelf' && (
        <div className="rounded-md border border-dashed border-border p-2 text-[11px] text-muted-foreground">
          100% — это буквально 100% занятости полки коробками, товар вплотную. Для большинства полок
          таргет по заполненности будет ниже 100%. Строки подобраны по АБСОЛЮТНОЙ загрузке (0–100%,
          с шагом 10), но метка в скобках — это та же загрузка относительно <code>max effective
          occupancy</code> этой полки: не таргет, а физический предел размещения товара. С какого-то
          ряда метка упрётся в «100%» — это нормально.
        </div>
      )}
      {data.rows.map((row) => (
        <div key={String(row.state)} className="space-y-1">
          <div className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{row.label}</span>
            {row.relative_label !== undefined && (
              <span> (относительно max effective: {row.relative_label}%)</span>
            )}
            {' — '}{row.percent}% ({row.count} фото за период)
          </div>
          {/* All 5 cells always rendered — a hole is a signal (too little data),
              not something to hide (#156: "обязательно заполнять все 5"). */}
          <div className="grid grid-cols-5 gap-2">
            {Array.from({ length: 5 }, (_, i) => row.examples[i]).map((ex, i) =>
              ex ? <ExampleCell key={i} example={ex} /> : (
                <div key={i} className="flex aspect-video items-center justify-center rounded-md border border-dashed border-border text-[10px] text-muted-foreground">
                  нет фото
                </div>
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Редактирование на месте (#1178) ─────────────────────────────────────────────
// Та же форма, что по «Редактировать» в зонировании (UnifiedEditForm), открывается прямо
// здесь: выбираем камеру полигона в app-state, грузим её полигоны, берём сущность из
// state — форма читает всё оттуда же, что и на канвасе. Кадра (превью кропа) тут нет —
// форма его показывает только когда он загружен.
interface EditReq { kind: CalibrationPolygonKind; id: string; cameraId: string; zoneId: string }

function useInlineEditor(onChanged: (zoneId: string) => void) {
  const actions = useAppActions();
  const { polygons } = useAppState();
  const [req, setReq] = useState<EditReq | null>(null);
  const [loading, setLoading] = useState(false);
  const open = async (p: CalibrationPolygon, zoneId: string) => {
    setReq({ kind: p.kind, id: p.id, cameraId: p.camera_id, zoneId });
    setLoading(true);
    try {
      actions.setCamera(p.camera_id);
      await loadPolygonData(p.camera_id, actions);
    } catch (e) {
      console.error(e);
      setReq(null);
    } finally {
      setLoading(false);
    }
  };
  const close = () => setReq(null);
  let node: React.ReactNode = null;
  if (req && !loading) {
    const common = { open: true, onClose: close, onSuccess: () => { close(); onChanged(req.zoneId); } };
    if (req.kind === 'rack') {
      const r = polygons.racks.entities[req.id];
      node = r ? <UnifiedEditForm {...common} initialType="rack" editingRack={r} /> : null;
    } else if (req.kind === 'shelf') {
      const sh = polygons.shelves.entities[req.id];
      node = sh ? <UnifiedEditForm {...common} initialType="shelf" editingShelf={sh} selectedRackId={sh.rack} /> : null;
    } else if (req.kind === 'table') {
      const t = polygons.tables.entities[req.id];
      node = t ? <UnifiedEditForm {...common} initialType="table" editingTable={t} /> : null;
    } else {
      const cz = polygons.checkoutZones.entities[req.id];
      node = cz ? <UnifiedEditForm {...common} initialType={req.kind} editingCheckoutZone={cz} /> : null;
    }
  }
  return { open, node, loading, loadingId: loading ? req?.id : null };
}

function CalibrationRow(
  { polygon, zoneId, shelves, rackModel, zoneName, onChanged, onEdit, editLoading }: {
    polygon: CalibrationPolygon;
    zoneId: string;
    /** Полки этой витрины — только для kind === 'rack'. */
    shelves: CalibrationPolygon[];
    /** Полка: модель её стеллажа — для подписи «как у стеллажа (…)». */
    rackModel?: ShelfModel;
    /** Режим «все филиалы»: имя филиала в строке. */
    zoneName?: string;
    /** Пороги/модель поменялись — перечитать полигоны этого филиала (свёрнутые строки, флаги). */
    onChanged: (zoneId?: string) => void;
    /** Открыть форму редактирования (как в зонировании) прямо здесь (#1178). */
    onEdit: (p: CalibrationPolygon, zoneId: string) => void;
    editLoading?: boolean;
  },
) {
  const [open, setOpen] = useState(false);
  // Гистограмма «весь стеллаж» (среднее по полкам) по умолчанию свёрнута — сбивает с толку
  // рядом с гистограммами полок (Алексей 14.10, #1557).
  const [rackChartOpen, setRackChartOpen] = useState(false);
  // Очередь: редактор полигона под спойлером (свёрнут) и пересчёт гистограммы по боксам после
  // сохранения или по черновику (Алексей 21.10).
  const [queuePolyOpen, setQueuePolyOpen] = useState(false);
  const [queueRecount, setQueueRecount] = useState<{ draft: Record<string, number[][]> | null; nonce: number }>({ draft: null, nonce: 0 });
  const [refreshKey, setRefreshKey] = useState(0);
  const changed = () => onChanged(zoneId);
  const [from, setFrom] = useState(daysAgo(14));
  // Алексей 21.10 (#7022): «при движении ползунков не меняются значения полей про трешхолды — надо чтоб
  // менялись на ходу когда отпускаю ползунок». Список калибровки перезагружается долго (отметки по
  // истории), поэтому отпущенный маркер сразу кладёт значения поверх polygon.thresholds; свежий
  // список с сервера их сбрасывает.
  const [thrPreview, setThrPreview] = useState<Record<string, number | boolean> | null>(null);
  useEffect(() => { setThrPreview(null); }, [polygon.thresholds]);
  const previewThresholds = (next: Record<string, number | boolean>) => setThrPreview((p) => ({ ...(p ?? {}), ...next }));
  const shownPolygon = thrPreview ? { ...polygon, thresholds: { ...polygon.thresholds, ...thrPreview } } : polygon;

  // В новой вкладке (Эдем 01.11 #8367): калибровка со всеми филиалами грузится долго, уходить с неё
  // нельзя. Зонирование берёт филиал, камеру и полигон из адреса (?zone, ?camera, ?edit).
  const openInZoning = (e: React.MouseEvent) => {
    e.stopPropagation();
    const q = new URLSearchParams({ zone: zoneId, camera: polygon.camera_id, edit: `${polygon.kind}:${polygon.id}` });
    window.open(`/management/zoning?${q}`, '_blank', 'noopener');
  };

  return (
    <Card>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/50"
        >
          {open ? <ChevronDown className="size-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="size-4 shrink-0 text-muted-foreground" />}
          {zoneName && <Tooltip content={zoneName}><span className="max-w-40 truncate text-xs text-muted-foreground">{zoneName}</span></Tooltip>}
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            {KIND_LABELS[polygon.kind]}
          </span>
          <span className="flex flex-col">
            <span className="font-medium text-foreground">{polygon.name}</span>
            {polygon.last_edit && <EditLine edit={polygon.last_edit} review={!!polygon.under_review} />}
            {polygon.alerts_7d && (
              <Tooltip content="Алерты валидаторам за 7 дней; ложные — валидатор ответил «полно» (витрина) или не подтвердил (стол, очередь)">
                <span className="text-[11px] text-muted-foreground">алертов {polygon.alerts_7d.n}, ложных {polygon.alerts_7d.false}</span>
              </Tooltip>
            )}
          </span>
          {polygon.rack_name && <span className="text-xs text-muted-foreground">на «{polygon.rack_name}»</span>}
          {/* #161: visible WITHOUT expanding — the whole point is catching a
              bad threshold at a glance while scrolling the collapsed list. */}
          {/* never-fires — сведение, не предупреждение (Алексей 21.10 #7019: «это не оранжевый warning»):
              у сопутствующей, напитков и т. п. полка всегда полная, алерт и не должен срабатывать. */}
          {polygon.diagnosis && (
            polygon.diagnosis.verdict === 'never-fires' ? (
              <Tooltip content={polygon.diagnosis.verdict}><span className="text-xs font-medium text-sky-700 dark:text-sky-300">
                ℹ {polygon.diagnosis.note}
              </span></Tooltip>
            ) : (
              <Tooltip content={polygon.diagnosis.verdict}><span className={cn('text-xs font-medium', DIAG_TEXT[polygon.diagnosis.severity ?? 'mid'])}>
                ⚠ {polygon.diagnosis.note}
              </span></Tooltip>
            )
          )}
          {polygon.flags?.map((f, i) => (
            <Tooltip key={`${f.code}:${i}`} content={f.code}><span className={cn('rounded px-1.5 py-0.5 text-[11px] font-medium', FLAG_CHIP[f.severity ?? 'mid'])}>
              {f.severity === 'info' ? f.note : `⚑ ${f.note}`}
            </span></Tooltip>
          ))}
          <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
            {rowSummary(polygon, polygon.kind === 'rack' && shelves.length ? shelves.length : undefined)}
          </span>
          <Tooltip content="Редактировать все поля — та же форма, что в зонировании"><button
            type="button"
            onClick={(e) => { e.stopPropagation(); void onEdit(polygon, zoneId); }}
            disabled={editLoading}
            className="flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-50"
          >
            {editLoading ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : <Pencil className="size-3.5" aria-hidden />}
            Редактировать
          </button></Tooltip>
          <Tooltip content="Открыть полигон на канвасе (вкладка Зонирование)"><button
            type="button"
            onClick={openInZoning}
            className="flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
            aria-label="Открыть полигон на канвасе (вкладка Зонирование)"
          >
            <ExternalLink className="size-3.5" aria-hidden />
          </button></Tooltip>
        </button>
        {open && (
          <div className="space-y-3 border-t border-border px-3 py-3">
            {polygon.kind !== 'shelf' && polygon.kind !== 'table' && polygon.kind !== 'queue' && polygon.kind !== 'rack' && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Смотреть с
              <DatePicker value={from} onChange={setFrom} />
            </label>
            )}
            <ThresholdFields polygon={shownPolygon} rackModel={rackModel} onSaved={() => { setRefreshKey((k) => k + 1); changed(); }} />
            {polygon.kind === 'queue' && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setQueuePolyOpen((o) => !o)}
                  className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  {queuePolyOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                  Полигон очереди — редактировать (гистограмма пересчитается по боксам)
                </button>
                {queuePolyOpen && (
                  <QueuePolygonEditor
                    czId={polygon.id}
                    cameraId={polygon.camera_id}
                    name={polygon.name}
                    areaIds={polygon.area_ids ?? []}
                    onPreview={(draft) => setQueueRecount((r) => ({ draft, nonce: r.nonce + 1 }))}
                    onSaved={() => { setQueueRecount((r) => ({ draft: null, nonce: r.nonce + 1 })); changed(); }}
                    onDiscard={() => setQueueRecount((r) => (r.draft ? { draft: null, nonce: r.nonce + 1 } : r))}
                  />
                )}
              </div>
            )}
            {polygon.kind === 'rack' && shelves.length > 0 && (
              <div className="space-y-2 border-l-2 border-border pl-2">
                <div className="text-xs font-medium text-muted-foreground">Полки этой витрины — {shelves.length}</div>
                {shelves.map((sh) => (
                  <CalibrationRow
                    key={sh.id}
                    polygon={sh}
                    zoneId={zoneId}
                    shelves={[]}
                    rackModel={polygon.model}
                    onChanged={onChanged}
                    onEdit={onEdit}
                    editLoading={editLoading}
                  />
                ))}
              </div>
            )}
            {polygon.kind === 'shelf'
              ? <ShelfThresholdChart shelfId={Number(polygon.threshold_entity_id ?? polygon.id)} refreshKey={refreshKey} onChanged={changed} onThresholdsPreview={previewThresholds} />
              : polygon.kind === 'table'
                // Две гистограммы — еда и грязная посуда — вместо одной по max (Алексей 09.10, #1376);
                // порог один на обе: алерт считается по max(еда, посуда).
                ? (
                  <div className="space-y-5">
                    <TrackDistributionChart key={`${refreshKey}-food`} kind="table" entityId={Number(polygon.id)} thresholdEntityId={polygon.threshold_entity_id} onChanged={changed} onThresholdsPreview={previewThresholds} score="food" title="Еда на столе" />
                    <TrackDistributionChart key={`${refreshKey}-dirty`} kind="table" entityId={Number(polygon.id)} thresholdEntityId={polygon.threshold_entity_id} onChanged={changed} onThresholdsPreview={previewThresholds} score="dirty" title="Грязная посуда" />
                    {/* Нативные объекты (меню, салфетницы, соль): в статус стола и алерт не входят,
                        гистограмма — чтобы видеть, что голова умеет (Алексей 14.10, #1516). */}
                    <TrackDistributionChart key={`${refreshKey}-native`} kind="table" entityId={Number(polygon.id)} thresholdEntityId={polygon.threshold_entity_id} onChanged={changed} onThresholdsPreview={previewThresholds} score="native" title="Нативные объекты (меню, салфетницы — на статус не влияют)" />
                  </div>
                )
              : polygon.kind === 'rack'
                ? (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setRackChartOpen((o) => !o)}
                      className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                    >
                      {rackChartOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                      Гистограмма всего стеллажа (среднее по полкам)
                    </button>
                    {rackChartOpen && <TrackDistributionChart key={refreshKey} kind="rack" entityId={Number(polygon.id)} thresholdEntityId={polygon.threshold_entity_id} onChanged={changed} onThresholdsPreview={previewThresholds} />}
                  </div>
                )
              : polygon.kind === 'queue'
                ? <TrackDistributionChart key={refreshKey} kind={polygon.kind} entityId={Number(polygon.id)} thresholdEntityId={polygon.threshold_entity_id} onChanged={changed} onThresholdsPreview={previewThresholds} queueRecount={queueRecount} />
                : <CalibrationMatrix key={refreshKey} polygon={polygon} zoneId={zoneId} from={new Date(from).toISOString()} />}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── «Все филиалы», сортировка и фильтры (AI197 #1165/#1166) ──────────────────
// Список по всем доступным аккаунту филиалам грузится филиал за филиалом (4 параллельно),
// сортировка/фильтр работают по ВСЕМУ загруженному, а рендер — порциями по мере скролла
// (строки свёрнуты, но их сотни). Сортировка «по умолчанию» = #161: плохие пороги наверх,
// дальше по типу трека.

interface CalRow { polygon: CalibrationPolygon; zoneId: string; zoneName: string }

function useAllBranchesPolygons(zones: ZoneDto[], enabled: boolean) {
  const [rows, setRows] = useState<CalRow[]>([]);
  const [done, setDone] = useState(0);
  const [nonce, setNonce] = useState(0);
  const list = useMemo(() => zones.filter((z) => !z.is_archived), [zones]);
  const listKey = list.map((z) => z.id).join(',');
  const listRef = useRef(list);
  listRef.current = list;
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setRows([]); setDone(0);
    const list = listRef.current;
    let i = 0;
    const worker = async () => {
      while (!cancelled && i < list.length) {
        const z = list[i++];
        try {
          const r = await getCalibrationPolygons(String(z.id));
          if (cancelled) return;
          setRows((prev) => [...prev, ...r.data.polygons.map((polygon) => ({ polygon, zoneId: String(z.id), zoneName: z.humanreadable_name }))]);
        } catch { /* филиал без ответа — пропускаем, счётчик всё равно двигаем */ }
        if (!cancelled) setDone((d) => d + 1);
      }
    };
    void Promise.all([worker(), worker(), worker(), worker()]);
    return () => { cancelled = true; };
  }, [enabled, listKey, nonce]);
  /** Перечитать один филиал и заменить его строки на месте. Полная перезагрузка (reload) очищает
   *  список — строки пересоздаются и раскрытая полка сворачивается после каждого сохранения
   *  порога; поэтому после правки порога/делителя — только этот филиал. */
  const reloadZone = useCallback(async (zoneId: string) => {
    const z = listRef.current.find((x) => String(x.id) === zoneId);
    if (!z) return;
    try {
      const r = await getCalibrationPolygons(zoneId);
      const fresh = r.data.polygons.map((polygon) => ({ polygon, zoneId, zoneName: z.humanreadable_name }));
      setRows((prev) => {
        const at = prev.findIndex((x) => x.zoneId === zoneId);
        const rest = prev.filter((x) => x.zoneId !== zoneId);
        if (at < 0) return [...rest, ...fresh];
        return [...rest.slice(0, at), ...fresh, ...rest.slice(at)];
      });
    } catch { /* не перечиталось — останутся прежние значения в свёрнутой строке */ }
  }, []);
  return { rows, done, total: list.length, reload: () => setNonce((n) => n + 1), reloadZone };
}

type SortKey = 'default' | 'zone' | 'name' | 'kind' | 'threshold' | 'divisor' | 'model' | 'diagnosis' | 'false_alerts';
const SORT_LABELS: Record<SortKey, string> = {
  default: 'по умолчанию (плохие пороги наверх)',
  diagnosis: 'по диагнозу и отметкам',
  false_alerts: 'по ложным алертам за 7 дн.',
  zone: 'по филиалу',
  name: 'по имени',
  kind: 'по типу',
  threshold: 'по порогу',
  divisor: 'по делителю',
  model: 'по нейросети',
};
type KindFilter = 'all' | CalibrationPolygonKind;
const KIND_FILTERS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'Все' }, { value: 'shelf', label: 'Полки' }, { value: 'rack', label: 'Витрины' },
  { value: 'table', label: 'Столы' }, { value: 'queue', label: 'Очередь' }, { value: 'cashier', label: 'Касса' },
  { value: 'staff', label: 'Сотрудники' }, { value: 'order_waiting', label: 'Ожидание' },
];

function thresholdOf(p: CalibrationPolygon): number | null {
  const v = p.thresholds.empty_shelf_score ?? p.thresholds.max_allowed_size;
  return typeof v === 'number' ? v : null;
}
function divisorOf(p: CalibrationPolygon): number | null {
  const v = p.thresholds.divisor;
  return typeof v === 'number' ? v : null;
}
// Отметки-факты (severity info, #3343) полигон проблемным не делают.
function alertFlags(p: CalibrationPolygon) {
  return (p.flags ?? []).filter((f) => f.severity !== 'info');
}
function isBad(p: CalibrationPolygon): boolean {
  return !!p.diagnosis || alertFlags(p).length > 0;
}
function defaultCompare(a: CalibrationPolygon, b: CalibrationPolygon): number {
  // «На проверке» (сутки после правки, Эдем 01.11 #8379) — отдельной группой внизу: чинил и ждёшь.
  const badA = a.under_review ? 2 : isBad(a) ? 0 : 1;
  const badB = b.under_review ? 2 : isBad(b) ? 0 : 1;
  if (badA !== badB) return badA - badB;
  return KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind);
}
function ago(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} ч назад`;
  return `${Math.round(h / 24)} дн. назад`;
}

/** «Эдем 2 ч назад: порог 60%→50%» под именем строки; сутки после правки — «на проверке». */
function EditLine({ edit, review }: { edit: CalibrationEdit; review: boolean }) {
  const what = edit.old == null && edit.new == null ? edit.label : `${edit.label} ${edit.old}→${edit.new}`;
  return (
    <Tooltip content={`${new Date(edit.at).toLocaleString('ru-RU')} — последняя правка калибровки${review ? '. Сутки после правки отметки считаются только по новым данным' : ''}`}>
      <span className="text-[11px] text-muted-foreground">
        {edit.who} {ago(edit.at)}: {what}
        {review && <span className="ml-1 rounded bg-sky-100 px-1 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">на проверке</span>}
      </span>
    </Tooltip>
  );
}

function sortRows(rows: CalRow[], sortBy: SortKey, dir: 1 | -1): CalRow[] {
  const numCmp = (x: number | null, y: number | null) => (x == null ? 1 : y == null ? -1 : (x - y) * dir);
  const strCmp = (x: string, y: string) => x.localeCompare(y, 'ru') * dir;
  return [...rows].sort((ra, rb) => {
    const a = ra.polygon; const b = rb.polygon;
    switch (sortBy) {
      case 'zone': return strCmp(ra.zoneName, rb.zoneName) || defaultCompare(a, b);
      case 'name': return strCmp(a.name, b.name);
      case 'kind': return (KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)) * dir || strCmp(a.name, b.name);
      case 'threshold': return numCmp(thresholdOf(a), thresholdOf(b));
      case 'divisor': return numCmp(divisorOf(a), divisorOf(b));
      case 'model': return strCmp(a.model ?? '~', b.model ?? '~') || defaultCompare(a, b);
      case 'diagnosis': return numCmp(a.diagnosis ? -a.diagnosis.badness : (alertFlags(a).length ? 0 : null), b.diagnosis ? -b.diagnosis.badness : (alertFlags(b).length ? 0 : null));
      // Больше ложных — выше; при равных — больше алертов всего (Эдем 02.11 #8533).
      case 'false_alerts': return numCmp(a.alerts_7d ? -a.alerts_7d.false : null, b.alerts_7d ? -b.alerts_7d.false : null)
        || numCmp(a.alerts_7d ? -a.alerts_7d.n : null, b.alerts_7d ? -b.alerts_7d.n : null);
      default: return defaultCompare(a, b);
    }
  });
}

const PAGE = 60;

export default function CalibrationPage() {
  usePageTitle('Калибровка полигонов', 'Пороги и примеры фото по каждому полигону — приёмка точки');
  const { currentZone } = useCurrentZone();
  const { data: zones = [] } = useZonesList();
  const zoneId = currentZone?.id ? String(currentZone.id) : '';
  // «Все филиалы» — из селектора филиала в шапке (#1177), своей кнопки на странице нет.
  const allBranches = useAppState().zone.allBranches;
  const [kind, setKind] = useState<KindFilter>('all');
  const [sortBy, setSortBy] = useState<SortKey>('default');
  const [dir, setDir] = useState<1 | -1>(1);
  const [visible, setVisible] = useState(PAGE);
  const orderRef = useRef<{ sig: string; idx: Map<string, number> } | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  // «Все филиалы»: после сохранения формы перечитываем только филиал полигона — полная
  // перезагрузка заново тянет все филиалы (долго и много трафика, Эдем 01.11 #8364).
  const editor = useInlineEditor((zid) => { if (allBranches) void all.reloadZone(zid); else void mutate(); });

  const { data, error, isLoading, mutate } = useSWR(
    zoneId && !allBranches ? ['calibration:polygons', zoneId] : null,
    () => getCalibrationPolygons(zoneId).then((r) => r.data.polygons),
    { revalidateOnFocus: false },
  );
  // Группа филиалов (#1969) — только её филиалы.
  const scope = useBranchScope();
  const scopedZones = useMemo(() => (scope.zoneIds ? zones.filter(z => scope.zoneIds!.has(String(z.id))) : zones), [zones, scope.zoneIds]);
  const all = useAllBranchesPolygons(scopedZones, allBranches);

  const rows: CalRow[] = useMemo(() => (allBranches
    ? all.rows
    : (data ?? []).map((polygon) => ({ polygon, zoneId, zoneName: currentZone?.humanreadable_name ?? '' }))),
  [allBranches, all.rows, data, zoneId, currentZone]);
  // #1178: стеллаж — ряд, его полки — субряды внутри. Плоский список полок — только когда
  // фильтр «Полки» или сортировка по полочной метрике (порог/делитель/нейросеть/диагноз).
  const nested = kind === 'all' && (sortBy === 'default' || sortBy === 'zone' || sortBy === 'name' || sortBy === 'kind');
  const shown = useMemo(() => {
    let base = kind === 'all' ? rows : rows.filter((r) => r.polygon.kind === kind);
    if (nested) {
      base = base.filter((r) => r.polygon.kind !== 'shelf').map((r) => {
        if (r.polygon.kind !== 'rack') return r;
        const sh = rows.filter((x) => x.zoneId === r.zoneId && x.polygon.kind === 'shelf' && x.polygon.rack_id === r.polygon.id);
        // Плохие полки поднимают свой стеллаж: диагноз/отметки полок — на строке стеллажа с именем полки.
        const flags = [...(r.polygon.flags ?? []), ...sh.flatMap((x) => (x.polygon.flags ?? []).map((f) => ({ ...f, note: `${x.polygon.name}: ${f.note}` })))]
          .sort((a, b) => SEVERITY_RANK[a.severity ?? 'mid'] - SEVERITY_RANK[b.severity ?? 'mid']);
        const diag = r.polygon.diagnosis ?? (() => {
          const d = sh.find((x) => x.polygon.diagnosis)?.polygon;
          return d?.diagnosis ? { ...d.diagnosis, note: `${d.name}: ${d.diagnosis.note}` } : undefined;
        })();
        // «На проверке» стеллаж целиком — только когда правлены все его проблемные полки (Эдем 02.11 #8434).
        const shelvesDone = sh.some((x) => x.polygon.under_review) && !sh.some((x) => isBad(x.polygon) && !x.polygon.under_review);
        return { ...r, polygon: { ...r.polygon, flags, diagnosis: diag, under_review: r.polygon.under_review || shelvesDone } };
      });
    }
    const sorted = sortRows(base, sortBy, dir);
    // Порядок держим до перезагрузки (Эдем 02.11 #8434): сохранил правку — строка остаётся на месте,
    // а не улетает вниз из-под руки. Пересортировка — при смене филиала/фильтра/сортировки и когда меняется
    // сам набор строк (догрузка «Все филиалы» по частям, удаление).
    const sig = `${allBranches}|${zoneId}|${kind}|${sortBy}|${dir}|${nested}`;
    const key = (r: CalRow) => `${r.zoneId}:${r.polygon.kind}:${r.polygon.id}`;
    const prev = orderRef.current;
    if (prev && prev.sig === sig && prev.idx.size === sorted.length && sorted.every((r) => prev.idx.has(key(r)))) {
      const pos = (r: CalRow) => prev.idx.get(key(r)) ?? Number.MAX_SAFE_INTEGER;
      return [...sorted].sort((a, b) => pos(a) - pos(b));
    }
    if (sorted.length) orderRef.current = { sig, idx: new Map(sorted.map((r, i) => [key(r), i])) };
    return sorted;
  }, [rows, kind, sortBy, dir, nested, allBranches, zoneId]);

  useEffect(() => { setVisible(PAGE); }, [allBranches, kind, sortBy, dir, zoneId]);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const exhausted = visible >= shown.length;
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || exhausted) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setVisible((v) => v + PAGE);
    }, { rootMargin: '600px' });
    io.observe(node);
    return () => io.disconnect();
  }, [exhausted, shown.length]);

  if (!zoneId && !allBranches) {
    return <div className="p-4 text-sm text-muted-foreground">Выберите филиал или «Все филиалы» в селекторе слева сверху.</div>;
  }
  const onChanged = (zid?: string) => {
    if (!allBranches) void mutate();
    else if (zid) void all.reloadZone(zid);
    else all.reload();
  };

  const help = (
    <div className="rounded-md border border-dashed border-border px-2 py-1 text-[11px] text-muted-foreground">
      <button type="button" onClick={() => setHelpOpen((o) => !o)} className="flex items-center gap-1 font-medium text-foreground">
        {helpOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        Как читать графики и пороги
      </button>
      {helpOpen && (
        <div className="mt-1 space-y-1">
          <p><b>Полка.</b> Ось — абсолютное (сырое) число модели, eff — то же после делителя, подписью под бинами. Количественная модель считает <b>ширину</b>: суммарная ширина боксов товара к длине полки вдоль её линии (боксы нарисованы на фото). Процентная — <b>площадь</b>: долю площади полки, занятую товаром. Полная полка редко даёт 100%, поэтому сырое делится на <b>делитель</b> (поправка модели → иначе конструктор максимума → иначе 0.8): eff = сырое / делитель. <b>Порог</b> (нижняя граница нормы) задан в eff: eff ниже — полка «ниже нормы». Порог и делитель перетаскиваются прямо на графике и сохраняются при отпускании; сменил делитель — порог смотри заново.</p>
          <p><b>Стеллаж.</b> Заполненность по модели — среднее по полкам; порога у стеллажа нет, алерт решают полки. <b>Стол.</b> Ось — скор «грязный» мультиголовы, порог один на всю систему (справочно). <b>Очередь.</b> Людей в очереди за минуту, порог — с какого числа очередь длинная (перетаскивается). Полигон очереди правится прямо в плашке (спойлер); после сохранения или «Пересчитать по черновику» гистограмма заново считается по сохранённым боксам людей под новый полигон, на фото — «было → стало», красная плашка — высланный алерт, который по новому полигону не ушёл бы.</p>
          <p>Цвет столбцов — статус итогового алерта по кадру. «Только кадры алертов» — те, по которым был ответ на алерт; «все замеры» — всё, с ответами там, где они были. Модели на одном графике не смешиваются.</p>
        </div>
      )}
    </div>
  );
  const controls = (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {allBranches && (
        <span className="text-muted-foreground">
          {all.done < all.total ? <><Loader2 className="mr-1 inline size-3 animate-spin" aria-hidden />загружено {all.done} из {all.total}</> : `${all.total} филиалов`} · {rows.length} полигонов
        </span>
      )}
      <ToggleGroup value={kind} onChange={(v) => setKind((v as KindFilter) ?? 'all')}>
        {KIND_FILTERS.map((k) => <ToggleGroupItem key={k.value} value={k.value}>{k.label}</ToggleGroupItem>)}
      </ToggleGroup>
      <label className="flex items-center gap-1 text-muted-foreground">
        Сортировка
        <Select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortKey)} className="h-7 text-xs text-foreground" wrapperClassName="w-auto">
          {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => <option key={k} value={k}>{SORT_LABELS[k]}</option>)}
        </Select>
        <Tooltip content="Направление"><button type="button" onClick={() => setDir((d) => (d === 1 ? -1 : 1))} className="rounded-md border border-border px-2 py-0.5 text-foreground hover:bg-muted">
          {dir === 1 ? '↑' : '↓'}
        </button></Tooltip>
      </label>
    </div>
  );

  if (!allBranches && isLoading) {
    return <div className="space-y-2 p-4">{controls}<div className="flex h-40 items-center justify-center"><Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden /></div></div>;
  }
  if (!allBranches && error && !data) {
    return <div className="space-y-2 p-4">{controls}<ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} /></div>;
  }

  return (
    <div className="space-y-2 p-4">
      {help}
      {controls}
      {/* Расписание вечернего понижения порога — редактор на фронте (Алексей 14.10, #1566). */}
      {!allBranches && zoneId && (
        <ThresholdScheduleCard zoneId={zoneId} racks={rows.filter((r) => r.polygon.kind === 'rack').map((r) => ({ id: r.polygon.id, name: r.polygon.name }))} />
      )}
      {shown.length === 0 && (all.done >= all.total || !allBranches) && (
        <div className="text-sm text-muted-foreground">{kind === 'all' ? 'На этом филиале ещё нет полигонов.' : 'Нет полигонов такого типа.'}</div>
      )}
      {shown.slice(0, visible).map(({ polygon: p, zoneId: zid, zoneName }) => (
        <CalibrationRow
          key={`${zid}-${p.kind}-${p.id}`}
          polygon={p}
          zoneId={zid}
          zoneName={allBranches ? zoneName : undefined}
          onChanged={onChanged}
          onEdit={editor.open}
          editLoading={editor.loadingId === p.id}
          rackModel={p.kind === 'shelf' ? rows.find((r) => r.zoneId === zid && r.polygon.kind === 'rack' && r.polygon.id === p.rack_id)?.polygon.model : undefined}
          shelves={p.kind === 'rack' && nested
            ? rows.filter((r) => r.zoneId === zid && r.polygon.kind === 'shelf' && r.polygon.rack_id === p.id).map((r) => r.polygon)
            : []}
        />
      ))}
      {editor.node}
      {!exhausted && <div ref={sentinelRef} className="flex h-10 items-center justify-center text-xs text-muted-foreground">ещё {shown.length - visible}…</div>}
    </div>
  );
}
